<template>
  <Teleport to="#appRoot">
    <BasePopup ref="popup" class="firstguide-popup" @hide="onClosePopup">
      <div ref="containerRef" class="firstguide-popup__container">
        <img
          class="firstguide-popup__sparkle"
          src="@/assets.app/images/twinkle.svg"
        />
        <h3>Your first guide is ready</h3>
        <div class="firstguide-popup__pane">
          <img :src="suggestion?.book.cover_image" width="120" height="160" />
          <AppButton
            class="firstguide-popup__button firstguide-popup--read-btn"
            icon="iadd"
            @click="onStartReading"
          >
            Start reading
          </AppButton>
          <AppButton
            class="firstguide-popup__button firstguide-popup--about-btn"
            :outlined="true"
            @click="onAboutBook"
          >
            About book
          </AppButton>

          <BookCardBackground
            v-if="suggestion"
            class="firstguide-popup__pane-bg"
            :doc="suggestion.book"
          />
        </div>
      </div>
    </BasePopup>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { storage } from '@/helpers/storage'
import { waitTimer } from '@/helpers/timer'
import { backend } from '@/services/backend'
import type { BookFirstGuideInfo } from '@/models/interfaces'
import { StorageKeys } from '@/helpers/storage.keys'

import { Mixpanel } from '@/analytics/mixpanel'
import BookCardBackground from '@/app/components/book/card/BookCardBackground.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import BasePopup from '@/components/ui/BasePopup.vue'

const router = useRouter()

const popup = ref<InstanceType<typeof BasePopup> | null>(null)
const suggestion = ref<BookFirstGuideInfo | null>(null)
// This is set when the user has pushed a button or closed the popup.
// Ensures we send exactly one action event.
const popupResolved = ref<boolean>(false)

onMounted(async () => {
  if (
    storage.getItem(StorageKeys.offerGuide) &&
    router.currentRoute.value.path !== '/app/billing'
  ) {
    await displayPopup()
  }
})

async function displayPopup(): Promise<void> {
  // Query the back end for the first guide, and (in parallel) wait
  // one second.
  const result: [BookFirstGuideInfo | null, any] = await Promise.all([
    backend.getExperimentFirstGuide(),
    waitTimer(1000),
  ])

  if (result[0]) {
    suggestion.value = result[0]
    storage.removeItem(StorageKeys.offerGuide)
    popup.value?.show()
    Mixpanel.trackFirstGuideAction(
      suggestion.value.book,
      router.currentRoute.value.path,
      'open',
    )
  }
}

function onStartReading(): void {
  if (suggestion.value) {
    popupResolved.value = true
    popup.value?.hide()

    Mixpanel.trackFirstGuideAction(
      suggestion.value.book,
      router.currentRoute.value.path,
      'read-book',
    )
    router.push({
      name: 'book.page',
      params: {
        url_slug: suggestion.value.book.url_slug,
        page_url_slug: suggestion.value.first_chapter_doc_slug,
      },
    })
  }
}

function onAboutBook(): void {
  if (suggestion.value) {
    popupResolved.value = true
    popup.value?.hide()

    Mixpanel.trackFirstGuideAction(
      suggestion.value.book,
      router.currentRoute.value.path,
      'about-book',
    )
    router.push({
      name: 'book.preview',
      params: {
        url_slug: suggestion.value.book.url_slug,
      },
    })
  }
}

function onClosePopup(): void {
  if (suggestion.value && !popupResolved.value) {
    popupResolved.value = true
    Mixpanel.trackFirstGuideAction(
      suggestion.value.book,
      router.currentRoute.value.path,
      'close',
    )
  }
  // The StorageKeys.offerGuide is already removed, so we shall not see the
  // popup again.
}
</script>

<style lang="scss" scoped>
.firstguide-popup {
  --popup-width: 600px;

  @media (max-height: 790px) {
    --popup-width: 462px;
  }

  :deep(.popup__window) {
    padding: 40px;

    @media (max-height: 790px) {
      padding: 40px 20px 20px;
    }

    @media (max-width: $w-mobile-s) {
      padding: 40px 12px 0 12px;
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;

    @media (max-height: 790px) {
      justify-content: flex-end;
    }

    h3 {
      margin-bottom: 16px;
      font-size: 32px;
      line-height: 36px;
      letter-spacing: -0.02em;
      text-align: center;
      color: $c-black-15;
      margin-bottom: 28px;

      @media (max-width: $w-mobile-s) {
        max-width: 335px;
      }

      @media (max-height: 790px) {
        font-size: 24px;
        line-height: 32px;
        margin-bottom: 12px;
      }
    }
  }

  &__sparkle {
    width: 40px;
    aspect-ratio: 1;
    margin-bottom: 12px;

    @media (max-width: $w-mobile-s) {
      width: 28px;
    }

    @media (max-height: 790px) {
      width: 28px;
    }
  }

  &__pane {
    position: relative;
    z-index: 0;
    overflow: hidden;
    width: 100%;
    border-radius: 18px;
    padding: 48px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;

    @media (max-width: $w-mobile-s) {
      border-radius: 18px 18px 0 0;
      justify-content: center;
      height: 100%;
    }

    @media (max-height: 790px) {
      padding-block: 18px;
    }

    img {
      width: 174px;
      aspect-ratio: 174 / 230;
      margin-bottom: 60px;
      border-radius: 20px;
      border: 2px solid rgba($c-black-2, 0.05);
      filter: $shadow-f;

      @media (max-height: 790px) {
        margin-bottom: 28px;
        width: 120px;
        border-radius: 14px;
      }
    }
  }

  &__pane-bg {
    opacity: 0.3;

    :deep(.book-card-background__inner) {
      // Make the base background image taller
      // to fit everything on taller phone screens.
      height: 60px;
    }
  }

  &__button {
    width: 100%;
    max-width: 360px;
    min-height: 56px;
    font-size: 18px;
    line-height: 22px;

    @media (max-height: 790px) {
      max-width: 300px;
      min-height: 0;
      font-size: 16px;
      line-height: 18px;
    }

    &:not(:last-of-type) {
      margin-bottom: 16px;

      @media (max-height: 790px) {
        margin-bottom: 8px;
      }
    }

    :deep(.iconfont) {
      margin-right: 4px;
      line-height: 1;
    }
  }
}

.night-mode {
  .firstguide-popup {
    &__container h3 {
      color: $c-black-5;
    }
  }
}
</style>
